import { useTranslation } from 'react-i18next';
import type { Transaction, TxType, TxStatus } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { copyText } from '../../lib/clipboard';
import { openLink } from '../../lib/links';
import { CoinIcon } from '../CoinIcon';
import { FigIcon, type FigIconName } from '../FigIcon';
import { Sheet } from './Sheet';
import { groupInt, fmtUsd, fmtDateHeading, fmtDateDMY, fmtTime } from '../../lib/format';

// Header glyph + title per transaction type. The circle is a blue vertical
// gradient (Figma 2:8365: from #2195f1 to #8ccdff) with a 44px white glyph.
const TYPE_META: Record<TxType, { icon: FigIconName; titleKey: string; typeKey: string }> = {
  receive: { icon: 'receive', titleKey: 'history.received', typeKey: 'txDetail.typeReceive' },
  send: { icon: 'send', titleKey: 'history.sent', typeKey: 'txDetail.typeSend' },
  topup: { icon: 'add', titleKey: 'history.topUp', typeKey: 'txDetail.typeTopup' },
  qr: { icon: 'qr', titleKey: 'history.qrPayment', typeKey: 'txDetail.typeQr' },
  buy: { icon: 'add', titleKey: 'history.bought', typeKey: 'txDetail.typeBuy' },
  fee: { icon: 'send', titleKey: 'history.sent', typeKey: 'txDetail.typeSend' },
};

const STATUS_META: Record<TxStatus, { key: string; className: string }> = {
  confirmed: { key: 'txDetail.statusConfirmed', className: 'text-positive' },
  pending: { key: 'txDetail.statusPending', className: 'text-warning' },
  failed: { key: 'txDetail.statusFailed', className: 'text-negative' },
};

function InfoRow({ label, value, valueClassName }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div className="flex w-full items-center justify-between">
      <p className="text-[15px] leading-none text-subtle">{label}</p>
      <p className={`text-right text-[15px] font-medium leading-none ${valueClassName ?? 'text-ink'}`}>{value}</p>
    </div>
  );
}

export function TransactionDetailSheet({ tx, onClose }: { tx: Transaction; onClose: () => void }) {
  const { t } = useTranslation();
  const m = TYPE_META[tx.type];
  const status = STATUS_META[tx.status];

  const abs = Math.abs(tx.amount);
  const sign = tx.amount > 0 ? '+' : tx.amount < 0 ? '-' : '';
  const num = abs >= 1 ? groupInt(abs) : abs.toString();
  const fee = tx.feeUsd ?? 0;
  const feeStr = fee === 0 ? `0 ${tx.coin}` : `~${fee < 1 ? fee : Math.round(fee)} ${tx.coin}`;

  // On-chain hash lives in the explorer URL; the row opens Tronscan on tap.
  const txHash = tx.explorerUrl?.split('/').pop() ?? tx.id;
  const shortHash = txHash.length > 18 ? `${txHash.slice(0, 8)}…${txHash.slice(-8)}` : txHash;

  const rows: { label: string; value: string; valueClassName?: string }[] = [
    { label: t('txDetail.amount'), value: `${num} ${tx.coin} (${fmtUsd(tx.amountUsd)})` },
    { label: t('txDetail.date'), value: fmtDateDMY(tx.date) },
    { label: t('txDetail.type'), value: t(m.typeKey) },
    { label: t('txDetail.status'), value: t(status.key), valueClassName: status.className },
    { label: t('txDetail.fee'), value: feeStr },
  ];

  // Store details, which only a QR payment or a card purchase carries. The chain
  // knows none of this — it comes from the acquirer.
  if (tx.merchant) rows.splice(1, 0, { label: t('txDetail.merchant'), value: tx.merchant });
  if (tx.storeAmount != null && tx.storeCurrency) {
    rows.splice(tx.merchant ? 2 : 1, 0, {
      label: t('txDetail.storeAmount'),
      value: `${tx.storeAmount.toFixed(2)} ${tx.storeCurrency}`,
    });
  }
  if (tx.paymentType) {
    rows.push({ label: t('txDetail.paymentType'), value: tx.paymentType.replace(/_/g, ' ') });
  }
  if (tx.mcc) rows.push({ label: t('txDetail.mcc'), value: tx.mcc });

  return (
    <Sheet
      onClose={onClose}
      panelClassName="flex w-full flex-col items-center gap-8 rounded-t-[36px] bg-surface px-4 pb-[44px] pt-4"
    >
        {/* top: title + icon + amount */}
        <div className="flex w-full flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-[6px]">
            <p className="text-[16px] font-medium text-ink">{t(m.titleKey)}</p>
            <p className="text-[14px] text-muted">
              {fmtDateHeading(tx.date)} {t('txDetail.at')} {fmtTime(tx.date)}
            </p>
          </div>

          <span
            className="grid size-20 place-items-center rounded-full text-white"
            style={{ background: 'linear-gradient(to bottom, #2195f1, #8ccdff)' }}
          >
            <FigIcon name={m.icon} size={44} />
          </span>

          <div className="flex h-11 flex-col items-center justify-center gap-2">
            <div className="flex items-center gap-1">
              <p className="font-bold leading-none">
                <span className="text-[24px] text-ink">{sign}{num} </span>
                <span className="text-[20px] text-muted">{tx.coin}</span>
              </p>
              <CoinIcon symbol={tx.coin} size={22} />
            </div>
            <p className="text-[14px] text-muted">{fmtUsd(tx.amountUsd)}</p>
          </div>
        </div>

        {/* info */}
        <div className="flex w-full flex-col gap-3">
          <p className="px-4 text-[15px] font-medium text-muted">{t('txDetail.info')}</p>
          <div className="flex w-full flex-col gap-4 rounded-[24px] bg-card p-4">
            {rows.map((r, i) => (
              <div key={r.label} className="flex flex-col gap-4">
                {i > 0 && <div className="h-px w-full bg-hairline" />}
                <InfoRow label={r.label} value={r.value} valueClassName={r.valueClassName} />
              </div>
            ))}

            {/* Transaction ID — tap opens the block explorer (Tronscan). */}
            <div className="flex flex-col gap-4">
              <div className="h-px w-full bg-hairline" />
              <button
                onClick={() => {
                  haptic();
                  if (tx.explorerUrl) openLink(tx.explorerUrl);
                }}
                className="flex w-full items-center justify-between gap-3 active:opacity-70"
              >
                <p className="shrink-0 text-[15px] leading-none text-subtle">
                  {t('txDetail.txId')}
                </p>
                <span className="flex items-center gap-2">
                  <span className="text-[15px] font-medium leading-none text-accent">{shortHash}</span>
                  <span
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void copyText(txHash);
                    }}
                  >
                    <FigIcon name="copy" size={16} className="text-muted" />
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
    </Sheet>
  );
}
